import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import { PinturaImageState } from '@pqina/pintura'
import { PhotoStudioHistoryEntry, PhotoStudioHistorySource } from './hooks/usePhotoStudioHistory.hook'

/** Enum representing edition types for analytics tracking */
export enum EditionType {
  VIRTUAL_STAGING = 'virtual_staging',
  MAGIC_ERASER = 'magic_eraser',
  ENHANCE = 'enhance',
  ADJUST = 'adjust',
}

/** Enum representing specific edition actions for analytics tracking */
export enum EditionAction {
  DECLUTTER_ROOM = 'declutter_room',
  STAGE_ROOM = 'stage_room',
  RESTYLE_ROOM = 'restyle_room',
  PAINT_WALLS = 'paint_walls',
  CHANGE_FLOOR = 'change_floor',
  ERASE = 'erase',
  ENHANCE = 'enhance',
  FINE_TUNE = 'fine_tune',
  CROP = 'crop',
  RESIZE = 'resize',
  BLUR = 'blur',
  ANNOTATE = 'annotate',
  STICKER = 'sticker',
  /** Adjust editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT) only: a filter look */
  FILTER = 'filter',
}

/** Maps GenerationType to EditionAction for analytics */
const GENERATION_TYPE_TO_EDITION_ACTION: Record<GenerationType, EditionAction> = {
  [GenerationType.DECLUTTER_ROOM]: EditionAction.DECLUTTER_ROOM,
  [GenerationType.EMPTY_ROOM]: EditionAction.STAGE_ROOM,
  [GenerationType.FURNISHED_ROOM]: EditionAction.RESTYLE_ROOM,
  [GenerationType.PAINT_WALLS]: EditionAction.PAINT_WALLS,
  [GenerationType.CHANGE_FLOOR]: EditionAction.CHANGE_FLOOR,
}

/** Maps Pintura feature name to EditionAction for analytics */
const PINTURA_FEATURE_TO_EDITION_ACTION: Record<string, EditionAction> = {
  finetune: EditionAction.FINE_TUNE,
  crop: EditionAction.CROP,
  resize: EditionAction.RESIZE,
  redact: EditionAction.BLUR,
  annotate: EditionAction.ANNOTATE,
  sticker: EditionAction.STICKER,
  filter: EditionAction.FILTER,
}

export const generationTypeToEditionAction = (
  generationType: GenerationType
): EditionAction => GENERATION_TYPE_TO_EDITION_ACTION[generationType]

export const pinturaFeatureToEditionAction = (
  pinturaFeature: string | undefined
): EditionAction | undefined => {
  if (!pinturaFeature) return undefined
  return PINTURA_FEATURE_TO_EDITION_ACTION[pinturaFeature]
}

/** Derives all EditionAction values from Pintura changes object */
export const getUsedEditionActions = (changes: Partial<PinturaImageState>): EditionAction[] => {
  const actions = new Set<EditionAction>()

  if (changes.crop || changes.rotation || changes.flipX || changes.flipY) {
    actions.add(EditionAction.CROP)
  }

  if (changes.colorMatrix || changes.convolutionMatrix || changes.gamma || changes.vignette) {
    actions.add(EditionAction.FINE_TUNE)
  }

  if (changes.annotation?.some((a: any) => a && !a.backgroundImage)) {
    actions.add(EditionAction.ANNOTATE)
  }

  if (changes.annotation?.some((a: any) => a && a.backgroundImage)) {
    actions.add(EditionAction.STICKER)
  }

  if (changes.redaction) {
    actions.add(EditionAction.BLUR)
  }

  if (changes.targetSize || changes.cropAspectRatio) {
    actions.add(EditionAction.RESIZE)
  }

  return Array.from(actions)
}

/**
 * Helper function to get the edition type string for analytics.
 * When editionType is 'adjust', it includes the Pintura feature name (e.g., 'adjust - crop').
 * @param editionType - The base edition type
 * @param selectedPinturaFeature - The selected Pintura feature (optional)
 * @returns The edition type string with Pintura feature appended if applicable
 */
export const getEditionTypeString = (
  editionType: EditionType | string | undefined,
  selectedPinturaFeature?: string
): string | undefined => {
  if (!editionType) {
    return undefined
  }

  // If it's 'adjust' and we have a Pintura feature selected, append it
  if (editionType === EditionType.ADJUST || editionType === 'adjust') {
    if (selectedPinturaFeature) {
      return `adjust - ${selectedPinturaFeature}`
    }
    return EditionType.ADJUST
  }

  return editionType
}

/** Enum for Photo Studio save modes */
export enum PhotoStudioSave {
  PHOTO_STUDIO_RE_EDIT_VISUAL = 'PHOTO_STUDIO_RE_EDIT_VISUAL',
  PHOTO_STUDIO_EDIT_VISUAL = 'PHOTO_STUDIO_EDIT_VISUAL',
}

/** Enum for Pintura save modes */
export enum PinturaSave {
  PINTURA_RE_EDIT_VISUAL = 'PINTURA_RE_EDIT_VISUAL',
  PINTURA_EDIT_VISUAL = 'PINTURA_EDIT_VISUAL',
}

/** Aggregates all unique EditionAction values from history entries up to the current cursor position */
export const aggregateSessionEditionActions = (
  entries: PhotoStudioHistoryEntry[],
  currentEntry: PhotoStudioHistoryEntry
): string[] | undefined => {
  const currentIndex = entries.findIndex(e => e.id === currentEntry.id)
  if (currentIndex === -1) return undefined

  const activeEntries = entries.slice(0, currentIndex + 1)
  const allActions = new Set<string>()

  for (const entry of activeEntries) {
    if (entry.editionSettings?.editionAction) {
      String(entry.editionSettings.editionAction).split(',').forEach(a => allActions.add(a.trim()))
    } else if (entry.source === PhotoStudioHistorySource.MAGIC_ERASER) {
      allActions.add(EditionAction.ERASE)
    } else if (entry.source === PhotoStudioHistorySource.VIRTUAL_STAGING && entry.editionSettings?.generationType) {
      const action = generationTypeToEditionAction(entry.editionSettings.generationType as GenerationType)
      if (action) allActions.add(action)
    }
  }

  return allActions.size > 0 ? Array.from(allActions) : undefined
}
